import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import ShopHourForm from './ShopHourForm';
import 'react-quill/dist/quill.snow.css';

const CreateShopHourModal = ({ show, onHide, onCreate, token }) => {
  const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = DateTime.utc().setZone(USER_TIMEZONE).toFormat("yyyy-MM-dd'T'HH:mm");

  const initialFormState = {
    name: '',
    description: '',
    startTime: now,
    endTime: now,
    backgroundImageUrl: '',
    backgroundMobileImageUrl: '',
    backgroundImageBrightness: '',
    backgroundMobileImageBrightness: '',
    backgroundThemeOverride: '',
    backgroundMobileThemeOverride: '',
    shopLogoImageUrl: '',
    shopButtonImageUrl: '',
    shopDescriptionImageUrl: '',
    shopDescription: '',
    active: false,
    shopHoursPassed: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [dateError, setDateError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData(initialFormState);
      setDateError('');
      setIsSaving(false);
    }
  }, [show]);

  const validateDates = () => {
    if (formData.endTime <= formData.startTime) {
      setDateError('End time must be after start time.');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (field === 'startTime' || field === 'endTime') {
      validateDates();
    }
  };

  const handleSubmit = async () => {
    if (!validateDates()) return;

    setIsSaving(true);
    try {
      const normalizeBrightness = value => {
        if (value === '' || value === null || value === undefined) {
          return null;
        }
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const normalizeTheme = value => (value ? value : null);

      const shopHourData = {
        ...formData,
        backgroundImageBrightness: normalizeBrightness(formData.backgroundImageBrightness),
        backgroundMobileImageBrightness: normalizeBrightness(
          formData.backgroundMobileImageBrightness
        ),
        backgroundThemeOverride: normalizeTheme(formData.backgroundThemeOverride),
        backgroundMobileThemeOverride: normalizeTheme(formData.backgroundMobileThemeOverride),
      };
      await onCreate({ shopHours: [shopHourData] });
      onHide();
    } catch (error) {
      console.error('Error creating shop hour:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" dialogClassName="shop-hour-modal">
      <Modal.Header closeButton>
        <Modal.Title>Create Shop Hour</Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-4">
        <ShopHourForm
          formData={formData}
          onChange={handleChange}
          dateError={dateError}
          timezoneLabel={USER_TIMEZONE}
          showShopHoursPassedToggle={false}
          token={token}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateShopHourModal;
